import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import {
  AppliedJobApiItem,
  AppliedJobStatistics,
  AppliedJobsFilter,
  AppliedJobsService,
} from './applied-jobs.service';

export interface Job {
  id: string;
  jobPostId: string;
  title: string;
  company: string;
  location: string;
  appliedDate: string;
  status:
    | 'Submitted'
    | 'Under Review'
    | 'Shortlisted'
    | 'Interviewing'
    | 'Rejected'
    | 'Accepted'
    | 'Selected For Interview';
  logoLetter: string;
  logoBg: string;
  logoColor: string;
  companyLogoUrl?: string;
  badge?: string;
  highlighted?: boolean;
  interviewUrl: string;
  interviewExpirationDate?: string;
  jobPostTitle: string;
  interviewCompleted: boolean;
  interviewExpired: boolean;
}

@Component({
  selector: 'app-applied-jobs',
  templateUrl: './applied-jobs.component.html',
  styleUrls: ['./applied-jobs.component.css'],
})
export class AppliedJobsComponent implements OnInit {
  currentPage = 1;
  pageSize = 10;
  totalPages = 1;
  loading = false;

  // ── Filter state ─────────────────────────────────────────────────────────
  filters: AppliedJobsFilter = {};

  statusOpen = false;
  jobTypeOpen = false;
  dateOpen = false;

  readonly statusOptions: { value: string; label: string }[] = [
    { value: 'SUBMITTED', label: 'Submitted' },
    { value: 'UNDER_REVIEW', label: 'Under Review' },
    { value: 'SHORTLISTED', label: 'Shortlisted' },
    { value: 'SELECTED_FOR_INTERVIEW', label: 'Selected For Interview' },
    { value: 'REJECTED', label: 'Rejected' },
  ];

  readonly jobTypeOptions: { value: string; label: string }[] = [
    { value: 'Full-time', label: 'Full-time' },
    { value: 'Part-time', label: 'Part-time' },
    { value: 'Contract', label: 'Contract' },
    { value: 'Internship', label: 'Internship' },
    { value: 'Remote', label: 'Remote' },
    { value: 'Freelance/Project', label: 'Freelance / Project' },
  ];

  // ── Data ──────────────────────────────────────────────────────────────────
  statistics: AppliedJobStatistics = {
    totalApplications: 0,
    interviews: 0,
    acceptanceRate: 0,
    activeApplications: 0,
  };
  jobs: Job[] = [];

  // ── Close dropdowns on outside click ─────────────────────────────────────
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    // If the click happened inside the filters bar, ignore it so local
    // click handlers can toggle dropdowns without being immediately closed.
    if (target && typeof target.closest === 'function') {
      if (
        target.closest('.filters-bar') ||
        target.closest('.filter-dropdown') ||
        target.closest('.dropdown-menu--calendar') ||
        target.closest('.flatpickr-calendar')
      ) {
        console.debug(
          '[AppliedJobs] document:click inside filter/calendar — ignoring',
        );
        return;
      }
    }
    console.debug('[AppliedJobs] document:click -> closing all dropdowns');
    this.statusOpen = false;
    this.jobTypeOpen = false;
    this.dateOpen = false;
  }

  // ── Pagination helpers ────────────────────────────────────────────────────
  get visiblePages(): number[] {
    const total = this.totalPages;
    const cur = this.currentPage;
    if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);
    if (cur <= 2) return [1, 2, 3];
    if (cur >= total - 1) return [total - 2, total - 1, total];
    return [cur - 1, cur, cur + 1];
  }

  get showEndEllipsis(): boolean {
    if (this.totalPages <= 3) return false;
    return this.visiblePages[this.visiblePages.length - 1] < this.totalPages;
  }

  // ── Filter label helpers ──────────────────────────────────────────────────
  get statusLabel(): string {
    if (!this.filters.status) return 'Status';
    const found = this.statusOptions.find(
      (o) => o.value === this.filters.status,
    );
    return found ? found.label : this.filters.status;
  }

  get jobTypeLabel(): string {
    if (!this.filters.jobType) return 'Job Type';
    const found = this.jobTypeOptions.find(
      (o) => o.value === this.filters.jobType,
    );
    return found ? found.label : this.filters.jobType;
  }
  get dateLabel(): string {
    return this.filters.appliedAt ?? 'Date Applied';
  }

  get hasStatusFilter(): boolean {
    return !!this.filters.status;
  }
  get hasJobTypeFilter(): boolean {
    return !!this.filters.jobType;
  }
  get hasDateFilter(): boolean {
    return !!this.filters.appliedAt;
  }

  constructor(
    private appliedJobsService: AppliedJobsService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadJobs(0);
  }

  // ── Data loading ──────────────────────────────────────────────────────────
  loadJobs(page: number): void {
    this.loading = true;
    this.appliedJobsService
      .getMyApplications(page, this.pageSize, this.filters)
      .subscribe({
        next: (response) => {
          this.statistics = response.statistics;
          this.jobs = (response.jobs.content || []).map((job) =>
            this.mapJob(job),
          );
          this.currentPage = page + 1;
          const totalElements: number =
            response.jobs.totalElements ??
            response.jobs.totalCount ??
            response.jobs.total ??
            null;

          if (totalElements !== null) {
            this.totalPages = Math.max(
              1,
              Math.ceil(totalElements / this.pageSize),
            );
          } else {
            // Fallback: use the page flags the API already provides
            this.totalPages = response.jobs.last
              ? this.currentPage
              : this.currentPage + 1;
          }

          this.loading = false;
        },
        error: (err) => {
          console.error('Failed to load applied jobs:', err);
          this.jobs = [];
          this.loading = false;
        },
      });
  }

  // ── Filter actions ────────────────────────────────────────────────────────
  toggleStatus(event: MouseEvent): void {
    event.stopPropagation();
    console.debug('[AppliedJobs] toggleStatus (before):', {
      statusOpen: this.statusOpen,
      hasStatusFilter: this.hasStatusFilter,
    });
    this.jobTypeOpen = false;
    this.dateOpen = false;
    this.statusOpen = !this.statusOpen;
    console.debug('[AppliedJobs] toggleStatus (after):', {
      statusOpen: this.statusOpen,
    });
  }

  toggleJobType(event: MouseEvent): void {
    event.stopPropagation();
    console.debug('[AppliedJobs] toggleJobType (before):', {
      jobTypeOpen: this.jobTypeOpen,
    });
    this.statusOpen = false;
    this.dateOpen = false;
    this.jobTypeOpen = !this.jobTypeOpen;
    console.debug('[AppliedJobs] toggleJobType (after):', {
      jobTypeOpen: this.jobTypeOpen,
    });
  }

  toggleDate(event: MouseEvent): void {
    event.stopPropagation();
    console.debug('[AppliedJobs] toggleDate (before):', {
      dateOpen: this.dateOpen,
    });
    this.statusOpen = false;
    this.jobTypeOpen = false;
    this.dateOpen = !this.dateOpen;
    console.debug('[AppliedJobs] toggleDate (after):', {
      dateOpen: this.dateOpen,
    });
    if (this.dateOpen) {
      // initialize flatpickr on open
      setTimeout(() => this.ensureFlatpickr(), 0);
    } else {
      // close/destroy any existing instance
      this.destroyFlatpickr();
    }
  }

  selectStatus(event: MouseEvent, value: string): void {
    event.stopPropagation();
    this.filters = { ...this.filters, status: value };
    this.statusOpen = false;
    this.loadJobs(0);
  }

  clearStatus(event: MouseEvent): void {
    event.stopPropagation();
    const { status, ...rest } = this.filters;
    this.filters = rest;
    this.loadJobs(0);
  }

  selectJobType(event: MouseEvent, value: string): void {
    event.stopPropagation();
    this.filters = { ...this.filters, jobType: value };
    this.jobTypeOpen = false;
    this.loadJobs(0);
  }

  clearJobType(event: MouseEvent): void {
    event.stopPropagation();
    const { jobType, ...rest } = this.filters;
    this.filters = rest;
    this.loadJobs(0);
  }

  onDateChange(event: Event): void {
    event.stopPropagation();
    const value = (event.target as HTMLInputElement).value;
    this.filters = { ...this.filters, appliedAt: value || undefined };
    if (value) {
      this.dateOpen = false;
      this.loadJobs(0);
    }
  }

  clearDate(event: MouseEvent): void {
    event.stopPropagation();
    const { appliedAt, ...rest } = this.filters;
    this.filters = rest;
    this.loadJobs(0);
  }

  stopProp(event: MouseEvent): void {
    console.debug('[AppliedJobs] stopProp');
    event.stopPropagation();
  }

  // ── Inline calendar state & helpers ──────────────────────────────────────
  // flatpickr instance (created on demand)
  private fpInstance: any = null;

  private buildCalendar(month: number, year: number): void {
    // legacy helper retained but unused when using Flatpickr
    return;
  }

  prevCalendarMonth(): void {
    // retained for compatibility; Flatpickr handles month navigation
  }

  nextCalendarMonth(): void {
    // retained for compatibility; Flatpickr handles month navigation
  }

  selectCalendarDate(iso: string): void {
    // retained for non-Flatpickr fallback
    this.filters = { ...this.filters, appliedAt: iso };
    this.dateOpen = false;
    this.loadJobs(0);
  }

  private async ensureFlatpickr(): Promise<void> {
    if (this.fpInstance) return;
    console.debug('[AppliedJobs] ensureFlatpickr: importing flatpickr...');
    // dynamic import to support ESM and avoid SSR issues
    let mod: any;
    try {
      mod = await import('flatpickr');
    } catch (err) {
      console.error('[AppliedJobs] ensureFlatpickr: import error', err);
      return;
    }
    const flatpickr = mod && mod.default ? mod.default : mod;
    console.debug(
      '[AppliedJobs] ensureFlatpickr: imported flatpickr',
      !!flatpickr,
      typeof flatpickr,
    );

    // Log DOM state
    const menuEl = document.querySelector(
      '.dropdown-menu--calendar',
    ) as HTMLElement | null;
    console.debug('[AppliedJobs] ensureFlatpickr: found menu?', !!menuEl);
    if (menuEl)
      console.debug(
        '[AppliedJobs] menu innerHTML before:',
        menuEl.innerHTML.substring(0, 100),
      );

    const container = document.querySelector(
      '.dropdown-menu--calendar .flatpickr-inline-container',
    ) as HTMLElement | null;
    console.debug(
      '[AppliedJobs] ensureFlatpickr: found container?',
      !!container,
      'container:',
      container,
    );
    if (!container) {
      console.warn('[AppliedJobs] ensureFlatpickr: container not found');
      return;
    }

    try {
      console.debug(
        '[AppliedJobs] ensureFlatpickr: calling flatpickr() with inline: true',
      );
      this.fpInstance = flatpickr(container, {
        inline: true,
        allowInput: false,
        dateFormat: 'Y-m-d',
        defaultDate: this.filters.appliedAt || null,
        onChange: (selectedDates: Date[], dateStr: string) => {
          console.debug('[AppliedJobs] flatpickr onChange:', dateStr);
          if (dateStr) {
            this.filters = { ...this.filters, appliedAt: dateStr };
            this.loadJobs(0);
          } else {
            const { appliedAt, ...rest } = this.filters;
            this.filters = rest;
            this.loadJobs(0);
          }
          this.dateOpen = false;
          setTimeout(() => this.destroyFlatpickr(), 0);
        },
      });
      console.debug(
        '[AppliedJobs] ensureFlatpickr: instance created',
        !!this.fpInstance,
      );
      console.debug(
        '[AppliedJobs] menu innerHTML after:',
        menuEl?.innerHTML.substring(0, 200),
      );
    } catch (err) {
      console.error('[AppliedJobs] ensureFlatpickr: init error', err);
      this.destroyFlatpickr();
    }
  }

  private destroyFlatpickr(): void {
    if (!this.fpInstance) return;
    try {
      this.fpInstance.destroy();
    } catch (e) {
      /* ignore */
    }
    this.fpInstance = null;
  }

  // legacy calendar helpers removed (Flatpickr used)

  onSelectChange(event: Event, type: 'status' | 'jobType'): void {
    const value = (event.target as HTMLSelectElement).value || undefined;
    if (type === 'status') {
      this.filters = { ...this.filters, status: value };
    } else {
      this.filters = { ...this.filters, jobType: value };
    }
    this.loadJobs(0);
  }

  // ── Pagination ────────────────────────────────────────────────────────────
  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.loadJobs(page - 1);
  }

  previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }
  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  viewJob(jobPostId: string): void {
    this.router.navigate(['/job-description', jobPostId]);
  }

  startInterview(interviewUrl: string): void {
    if (!interviewUrl) {
      return;
    }
    this.router.navigate([interviewUrl]);
  }

  // ── Mapping helpers ───────────────────────────────────────────────────────
  private mapJob(job: AppliedJobApiItem): Job {
    const seed = this.hashString(job.companyName || job.positionTitle);
    const palette = this.getPalette(seed);
    const isExpired = job.interviewExpired === true ||
      (!!job.interviewExpirationDate && new Date(job.interviewExpirationDate) < new Date());
    return {
      id: job.id,
      jobPostId: job.jobPostId,
      title: job.positionTitle,
      jobPostTitle: job.jobPostTitle || job.positionTitle,
      company: job.companyName,
      location: '',
      appliedDate: this.formatDate(job.appliedAt),
      status: this.mapStatus(job.status),
      logoLetter: this.getInitials(job.companyName || job.positionTitle),
      logoBg: palette.background,
      logoColor: palette.color,
      companyLogoUrl: this.normalizeAssetUrl(job.companyLogoUrl),
      interviewUrl: job.interviewUrl || '',
      interviewExpirationDate: this.formatDateTime(job.interviewExpirationDate),
      interviewCompleted: !!(job.interviewUrl && !isExpired),
      interviewExpired: isExpired,
    };
  }

  private mapStatus(status: string): Job['status'] {
    const s = status.trim().toUpperCase();
    if (['SUBMITTED'].includes(s)) return 'Submitted';
    if (['SHORTLISTED', 'SHORTLIST'].includes(s)) return 'Shortlisted';
    if (['SELECTED_FOR_INTERVIEW'].includes(s)) return 'Selected For Interview';
    if (['INTERVIEW', 'INTERVIEWING', 'SCHEDULED_INTERVIEW'].includes(s))
      return 'Interviewing';
    if (['REJECTED', 'DECLINED', 'CANCELLED'].includes(s)) return 'Rejected';
    if (['ACCEPTED', 'OFFERED', 'HIRED'].includes(s)) return 'Accepted';
    return 'Under Review';
  }

  private formatDate(value: string): string {
    const d = new Date(value);
    return isNaN(d.getTime())
      ? value
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  private formatDateTime(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  formatInterviewExpiry(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private normalizeAssetUrl(value?: string): string | undefined {
    if (!value) return undefined;
    const cleaned = value.replace(/\\/g, '/');
    if (/^https?:\/\//i.test(cleaned)) return cleaned;
    return `${environment.apiUrl.replace(/\/+$/, '')}/${cleaned.replace(/^\/+/, '')}`;
  }

  private getInitials(value: string): string {
    const parts = value.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'J';
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  private hashString(value: string): number {
    let h = 0;
    for (let i = 0; i < value.length; i++) {
      h = (h << 5) - h + value.charCodeAt(i);
      h |= 0;
    }
    return Math.abs(h);
  }

  private getPalette(seed: number): { background: string; color: string } {
    const p = [
      { background: '#eef3f6', color: '#0099cf' },
      { background: '#dfe9ff', color: '#155bdb' },
      { background: '#f3f4f6', color: '#6b7280' },
      { background: '#e8f7ef', color: '#0f9d58' },
      { background: '#fff1e8', color: '#d97706' },
    ];
    return p[seed % p.length];
  }

  getStatusClass(status: string): string {
    const map: Record<string, string> = {
      Submitted: 'status--submitted',
      'Under Review': 'status--review',
      Shortlisted: 'status--shortlisted',
      'Selected For Interview': 'status--selected-interview',
      Interviewing: 'status--interviewing',
      Rejected: 'status--rejected',
      Accepted: 'status--accepted',
    };
    return map[status] || '';
  }
}
